// Capability catalog, role-default policy, and the pure helpers over them — the
// one definition both packages consume (backend/src/policy/capabilities.ts and
// frontend/src/types/common.ts re-export from here), so a UI gate can never
// disagree with the check the backend actually enforces.
//
// The import below must stay type-only: the frontend imports this module for
// its runtime helpers, and its Next/webpack build has no extensionAlias, so a
// value import of "./types/common.js" would type-check and then fail to
// resolve at build time.
import type { AuthUser, UserRole } from "./types/common.js";

export type SessionRoleBand = "admin" | "agent" | "closer" | "team_leader";

const CAPABILITIES = {
  "contacts.view": {
    group: "Contacts",
    label: "View contacts",
    description: "Open contact list and contact detail screens.",
  },
  "contacts.activity.view": {
    group: "Contacts",
    label: "View contact activity",
    description: "Read contact activity and CRM history.",
  },
  "contacts.send": {
    group: "Contacts",
    label: "Send contacts",
    description: "Send a contact you own to another agent, moving campaign ownership and pending callbacks.",
  },
  "campaigns.view": {
    group: "Campaigns",
    label: "View campaigns",
    description: "See campaign context without necessarily using the dialing queue.",
  },
  "campaigns.dial": {
    group: "Campaigns",
    label: "Use campaign dialer",
    description: "Use campaigns as an agent-style dialing queue.",
  },
  "insights.view_self": {
    group: "Insights",
    label: "View own insights",
    description: "See personal performance dashboards.",
  },
  "insights.view_team": {
    group: "Insights",
    label: "View team insights",
    description: "See team/admin insights and setter statistics.",
  },
  "deals.view": {
    group: "Deals",
    label: "View deals",
    description: "Open deal boards and deal details.",
  },
  "billing.view": {
    group: "Deals",
    label: "View billing",
    description: "Open the /billing dashboard: payment plans, charges, and their statuses for visible deals (admins see all, team leads their closers', closers their own).",
  },
  "deals.assign_closer": {
    group: "Deals",
    label: "Assign deal closer",
    description: "Change the owner/closer responsible for a deal.",
  },
  "meetings.view": {
    group: "Meetings",
    label: "View meetings",
    description: "Open meetings/calendar screens.",
  },
  "meetings.book": {
    group: "Meetings",
    label: "Book meetings",
    description: "Create booked meetings for contacts.",
  },
  "meetings.assign_closer": {
    group: "Meetings",
    label: "Assign meeting closer",
    description: "Transfer a meeting to another closer.",
  },
  "meetings.manage_availability": {
    group: "Meetings",
    label: "Manage closer availability",
    description: "Set closer working hours and blocked times that gate meeting bookings.",
  },
  "meetings.view_booking_score": {
    group: "Meetings",
    label: "Can see booking evaluation score",
    description: "See the booking-call evaluation (how likely the prospect is to attend) on meetings, contact meeting cards, activities, reports and insights.",
  },
  "meetings.view_review_score": {
    group: "Meetings",
    label: "Can see meeting review score",
    description: "See the AI review of how a meeting was held (overall and likelihood-to-pay scores) on meetings, recordings and the Quality page.",
  },
  "messages.send": {
    group: "Messages",
    label: "Send messages",
    description: "Write and send emails/texts to contacts from the message composer.",
  },
  "templates.manage": {
    group: "Messages",
    label: "Manage message templates",
    description: "Create and use reusable email/SMS templates on the /templates page and in the composer.",
  },
  "sequences.manage": {
    group: "Messages",
    label: "Manage sequences",
    description: "Create manual message sequences on the /sequences page and enroll open deals in them.",
  },
  "callbacks.view_team": {
    group: "Callbacks",
    label: "View team callbacks",
    description: "See team callback queues.",
  },
  "callbacks.manage_team": {
    group: "Callbacks",
    label: "Manage team callbacks",
    description: "Complete, cancel, or reassign team callbacks.",
  },
  "dialer.manual_dial": {
    group: "Dialer",
    label: "Manual dial",
    description: "Place manual outbound calls outside the assigned campaign queue.",
  },
  "dialer.use_softphone": {
    group: "Dialer",
    label: "Use softphone",
    description: "Show the softphone widget and call controls.",
  },
  "users.view": {
    group: "Users",
    label: "View users",
    description: "View user roster/context.",
  },
  "users.manage": {
    group: "Users",
    label: "Manage users",
    description: "Create, deactivate, and update users.",
  },
  "roles.manage_capabilities": {
    group: "Roles",
    label: "Manage role capabilities",
    description: "Edit the role matrix and future user overrides.",
  },
} as const;

export type Capability = keyof typeof CAPABILITIES;

export const CAPABILITY_DEFINITIONS = CAPABILITIES;

export const ALL_CAPABILITIES = Object.keys(CAPABILITIES) as Capability[];

export function isCapability(value: string): value is Capability {
  return value in CAPABILITIES;
}

export type OverridableRole = Exclude<UserRole, "admin">;

export const OVERRIDABLE_ROLES: readonly OverridableRole[] = ["team_leader", "setter", "closer", "booker"];

// Capabilities managed per USER, not per role — excluded from the role matrix.
// meetings.book never worked as a role override: the per-user "Book meetings"
// toggle re-adds it for setters after the override subtracts it, so the matrix
// checkbox read as off while setters kept booking. Booking stays: setters via
// their user toggle, closers/bookers via role defaults. Stored override rows
// for these are ignored on load and rejected on write.
export const USER_SCOPED_CAPABILITIES: readonly Capability[] = ["meetings.book"];

export function isOverridableRole(value: string): value is OverridableRole {
  return (OVERRIDABLE_ROLES as readonly string[]).includes(value);
}

// Admin-toggled deltas from the role defaults below; absence means default.
export type RoleCapabilityOverrides = Partial<Record<OverridableRole, Partial<Record<Capability, boolean>>>>;

export const ROLE_DEFAULT_CAPABILITIES: Record<OverridableRole, readonly Capability[]> = {
  team_leader: [
    "contacts.view",
    "contacts.activity.view",
    "contacts.send",
    "campaigns.view",
    "insights.view_self",
    "insights.view_team",
    "deals.view",
    "billing.view",
    "deals.assign_closer",
    "meetings.view",
    "meetings.assign_closer",
    "meetings.manage_availability",
    "meetings.view_booking_score",
    "meetings.view_review_score",
    "messages.send",
    "templates.manage",
    "sequences.manage",
    "callbacks.view_team",
    "callbacks.manage_team",
    "dialer.use_softphone",
    "users.view",
  ],
  setter: [
    "contacts.view",
    "campaigns.view",
    "campaigns.dial",
    "insights.view_self",
    "meetings.view",
    "meetings.view_booking_score",
    "callbacks.view_team",
    "dialer.use_softphone",
  ],
  closer: [
    "contacts.view",
    "contacts.activity.view",
    "campaigns.view",
    "campaigns.dial",
    "insights.view_self",
    "deals.view",
    "billing.view",
    "meetings.view",
    "meetings.book",
    "meetings.manage_availability",
    "meetings.view_booking_score",
    "meetings.view_review_score",
    "messages.send",
    "templates.manage",
    "sequences.manage",
    "callbacks.view_team",
    "dialer.manual_dial",
    "dialer.use_softphone",
  ],
  booker: [
    "contacts.view",
    "contacts.send",
    "campaigns.view",
    "campaigns.dial",
    "insights.view_self",
    "meetings.view",
    "meetings.book",
    "meetings.view_booking_score",
    "messages.send",
    "callbacks.view_team",
    "dialer.manual_dial",
    "dialer.use_softphone",
  ],
};

export function getEffectiveCapabilities(
  user: Pick<AuthUser, "role" | "authorizedToBookMeetings" | "manualDialingEnabled">,
  overrides?: RoleCapabilityOverrides
): Capability[] {
  if (user.role === "admin") {
    return [...ALL_CAPABILITIES];
  }

  const capabilities = new Set<Capability>(ROLE_DEFAULT_CAPABILITIES[user.role]);

  const roleOverrides = overrides?.[user.role];
  if (roleOverrides) {
    for (const [capability, enabled] of Object.entries(roleOverrides)) {
      if (enabled) {
        capabilities.add(capability as Capability);
      } else {
        capabilities.delete(capability as Capability);
      }
    }
  }

  // Per-user grants sit on top of the role matrix.
  if (user.role === "setter" && user.authorizedToBookMeetings) {
    capabilities.add("meetings.book");
  }
  if (user.role === "setter" && user.manualDialingEnabled) {
    capabilities.add("dialer.manual_dial");
  }

  return [...capabilities];
}

export function hasCapability(
  user: Pick<AuthUser, "role" | "authorizedToBookMeetings" | "manualDialingEnabled" | "capabilities">,
  capability: Capability
): boolean {
  if (user.role === "admin") {
    return true;
  }
  if (user.capabilities) {
    return user.capabilities.includes(capability);
  }
  return getEffectiveCapabilities(user).includes(capability);
}

export function isAdminRole(role: UserRole): role is "admin" {
  return role === "admin";
}

export function isAgentRole(role: UserRole): role is "setter" | "closer" | "booker" {
  return role === "setter" || role === "closer" || role === "booker";
}

export function isTeamLeaderRole(role: UserRole): role is "team_leader" {
  return role === "team_leader";
}

export function isLeadershipRole(role: UserRole): role is "admin" | "team_leader" {
  return role === "admin" || role === "team_leader";
}

export function isCloserRole(role: UserRole): role is "closer" {
  return role === "closer";
}

// Leadership (admins and team leaders — there is no per-team leader mapping)
// or the closer who holds the meeting. Setters and bookers never qualify, even
// when recorded as the meeting's closer.
export function isMeetingCloserOrLeadership(
  user: Pick<AuthUser, "id" | "role">,
  closerUserId: string | null | undefined
): boolean {
  return isLeadershipRole(user.role) || (isCloserRole(user.role) && closerUserId === user.id);
}

export function canBookMeetings(user: Pick<AuthUser, "role" | "authorizedToBookMeetings" | "manualDialingEnabled" | "capabilities">): boolean {
  return hasCapability(user, "meetings.book");
}

// The warm-callback SEND gate. Historically this doubled as the whole handoff-family gate via the
// booking/handoff class split ("setter && !authorizedToBookMeetings"); the four booking-path switches
// (Book meetings / Warm callbacks / Hot transfers / AI transfers) are now independent per-user toggles.
// The `!== false` is a deliberate tri-state default, not a redundant comparison:
// callers can hold rows where the flag is null/undefined even though AuthUser
// declares it boolean, and those must still count as allowed.
export function canScheduleBookingCalls(user: Pick<AuthUser, "role" | "canSendWarmCallbacks">): boolean {
  return user.role === "setter" && user.canSendWarmCallbacks !== false;
}

export function canManualDial(user: Pick<AuthUser, "role" | "authorizedToBookMeetings" | "manualDialingEnabled" | "capabilities">): boolean {
  return hasCapability(user, "dialer.manual_dial");
}
